import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  getFullName,
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import OnboardingContactDropdown from './OnboardingContactDropdown';
import { sessionSelector } from '../../../reducers/session';
import { Tooltip } from '../../../widgets/Tooltip';
import { MSP_SUFFIX } from '../../../constants';
import Icon from '../../../widgets/Icons';

export const PrimarySecondary = () => {
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const primaryMSPContacts = useSelector(
    memoizedOnboardingSelectors.getPrimaryMSPContacts,
  );

  return (
    <div>
      <p className="ob-section-description">
        Designate contacts as authorized to make account changes. Only one
        contact can be the primary contact.
      </p>
      <div className="pl-1">
        <h5 className="pb-1">Primary Contact</h5>
        {!primaryMSPContacts.length && (
          <OnboardingContactDropdown
            placeholder="Choose a primary contact"
            contactAttr="contactType"
            disabled={!canEdit}
            id="selectPrimaryContact"
          />
        )}
        {!!primaryMSPContacts.length && (
          <span style={{ display: 'flex', alignItems: 'baseline' }}>
            {getFullName(primaryMSPContacts[0])}
            <div style={{ marginBottom: 1 }}>{MSP_SUFFIX}</div>
            <div style={{ marginLeft: 5 }}>
              <Tooltip
                text="Please contact your CST to make changes."
                placement="right"
                icon={<Icon.Pencil />}
              />
            </div>
          </span>
        )}
      </div>
      <div className="pl-1 pt-4">
        <h5 className="pb-1">Secondary Contacts</h5>
        <OnboardingContactDropdown
          placeholder="Choose a secondary contact"
          contactAttr="contactType"
          disabled={!canEdit}
          id="selectSecondaryContact"
        />
      </div>
      <div className="pl-1 pt-4">
        <h5 className="pb-1">Executive Sponsors</h5>
        <OnboardingContactDropdown
          placeholder="Choose an executive sponsor"
          contactAttr="isExecutiveSponsor"
          disabled={!canEdit}
          id="selectExecutiveSponsor"
        />
      </div>
    </div>
  );
};

export const Escalation = () => {
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  return (
    <div>
      <p className="ob-section-description">
        Add contacts to be notified when a security incident is detected.
        Escalation contacts can be modified at any time.
      </p>
      <div className="pl-1 pt-2">
        <h5 className="pb-1">Basic Escalation Contacts</h5>
        <OnboardingContactDropdown
          placeholder="Choose a basic escalation contact"
          contactAttr="isBasicEscalationContact"
          disabled={!canEdit}
          requireEmail={true}
          id="selectBasicEscalationContact"
        />
      </div>
    </div>
  );
};

export const Report = () => {
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  return (
    <div>
      <p className="ob-section-description">
        Receive notifications for reports by email when they become available.
      </p>
      <div className="pl-1 pt-4">
        <div className="d-flex align-items-center">
          <h5 className="pb-1 mr-2">Report Recipients</h5>
          <Tooltip
            text="Report contains security incident details."
            placement="bottom"
            style={{ paddingBottom: '5px' }}
          />
        </div>
        <OnboardingContactDropdown
          placeholder="Choose a report recipient"
          contactAttr="isReportRecipient"
          disabled={!canEdit}
          id="selectReportRecipient"
        />
      </div>
    </div>
  );
};

export const AccessControl = () => {
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const isManagedRiskEnabled = useSelector(
    onboardingSelector.isManagedRiskEnabled,
  );
  const isLogSearchEnabled = useSelector(onboardingSelector.isLogSearchEnabled);
  const isManagedAwarenessEnabled = useSelector(
    onboardingSelector.isManagedAwarenessEnabled,
  );
  const hasIRRProduct = useSelector(onboardingSelector.hasIRRProduct);
  return (
    <div>
      <p className="ob-section-description">
        Grant access to secure websites and services provided by Arctic Wolf.
        Only users can be granted access.
      </p>
      <div className="pl-1 pt-2">
        <div className="d-flex align-items-center">
          <h5 className="pb-1 mr-2">Portal</h5>
          <Tooltip
            text="Contains the security incident dashboard, traffic flow data, tickets, and connected accounts."
            placement="bottom"
            style={{ paddingBottom: '5px' }}
          />
        </div>
        <OnboardingContactDropdown
          placeholder="Choose a portal user"
          contactAttr="isPortalUser"
          disabled={!canEdit}
          id="selectPortalUser"
        />
      </div>
      {isLogSearchEnabled && (
        <div className="pl-1 pt-4">
          <h5 className="pb-1">Log Search Access</h5>
          <p className="ob-input-description">Contains log source data</p>
          <OnboardingContactDropdown
            placeholder="Choose a log search user"
            contactAttr="isLogSearchUser"
            disabled={!canEdit}
            id="selectLogSearchUser"
          />
        </div>
      )}
      {isManagedRiskEnabled && (
        <div className="pl-1 pt-4">
          <h5 className="pb-1">Risk Dashboard Access</h5>
          <p className="ob-input-description">
            Contains the managed risk dashboard and internal scan configuration
          </p>
          <OnboardingContactDropdown
            placeholder="Choose a risk dashboard user"
            contactAttr="isRiskUser"
            disabled={!canEdit}
            id="selectRiskUser"
          />
        </div>
      )}
      {isManagedAwarenessEnabled && (
        <div className="pl-1 pt-4">
          <h5 className="pb-1">Managed Awareness Access</h5>
          <OnboardingContactDropdown
            placeholder="Choose a managed awareness user"
            contactAttr="isManagedAwarenessUser"
            disabled={!canEdit}
            id="selectManagedAwarenessUser"
          />
        </div>
      )}
      <div className="pl-1 pt-4">
        <h5 className="pb-1">Activate Virtual Appliance Access</h5>
        <p className="ob-input-description">
          Users who will be responsible for deploying virtual machines and
          activation
        </p>
        <OnboardingContactDropdown
          placeholder="Choose users"
          contactAttr="isActivateVlcUser"
          disabled={!canEdit}
          id="selectActivateVlcUser"
        />
      </div>
      {hasIRRProduct && (
        <div className="pl-1 pt-4">
          <h5 className="pb-1">IR Planner</h5>
          <OnboardingContactDropdown
            placeholder="Choose an IR Planner user"
            contactAttr="isMyCyberDashboardUser"
            disabled={!canEdit}
            id="selectMyCyberDashboardUser"
          />
        </div>
      )}
    </div>
  );
};
